import type { RouterInputs } from "@utils/trpc";
import {
  createPlayOutbox as createOutbox,
  type PlayOutboxPorts,
} from "./playOutboxCore";

export type PlayOutboxDependencies = Omit<
  PlayOutboxPorts,
  "online" | "onForeground" | "beforeDrain" | "owner"
> & {
  currentUid(): string | undefined;
  network: PlayOutboxPorts["online"];
  appState: { subscribe(listener: (state: string) => void): () => void };
  legacyStorage: {
    getString(key: string): string | undefined;
    set(key: string, value: string): void;
    remove(key: string): void;
  };
  sendLegacy(input: RouterInputs["analytics"]["trackStream"]): Promise<unknown>;
};

export function createPlayOutbox({
  currentUid,
  network,
  appState,
  legacyStorage,
  sendLegacy,
  ...ports
}: PlayOutboxDependencies) {
  type Legacy = RouterInputs["analytics"]["trackStream"] & {
    queued_at?: string;
  };
  let legacyFlush: Promise<void> | undefined;
  let legacyGeneration = 0;
  let disposed = false;
  const legacyKeys = ["stream_event_queue", "play_event_queue_v1"];
  function readLegacy(key: string) {
    const raw = legacyStorage.getString(key);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Legacy[] | { legacy?: Legacy[] };
    return {
      events: Array.isArray(parsed) ? parsed : (parsed.legacy ?? []),
      save(events: Legacy[]) {
        if (!events.length) legacyStorage.remove(key);
        else
          legacyStorage.set(
            key,
            JSON.stringify(
              Array.isArray(parsed) ? events : { ...parsed, legacy: events },
            ),
          );
      },
    };
  }
  /** Keep failed legacy replays and other accounts' evidence for their next sign-in. */
  function flushLegacyPlayQueue(): Promise<void> {
    if (disposed) return Promise.resolve();
    const epoch = legacyGeneration;
    const account = currentUid() ?? "";
    legacyFlush ??= (async () => {
      for (const key of legacyKeys) {
        const queue = readLegacy(key);
        if (!queue) continue;
        let remaining = queue.events.map((event) =>
          event.userId === account
            ? { ...event, eventId: event.eventId ?? ports.newPlayId() }
            : event,
        );
        const events = remaining.filter((event) => event.userId === account);
        if (!events.length) continue;
        // Persist chunk identities before sending: the process may die after acceptance.
        queue.save(remaining);
        for (const [index, event] of events.entries()) {
          if (epoch !== legacyGeneration) return;
          const { queued_at, ...input } = event;
          try {
            await sendLegacy({
              ...input,
              lsn_timestamp: queued_at ?? input.lsn_timestamp,
              complete: !events
                .slice(index + 1)
                .some((e) => e.playback_id === input.playback_id),
            });
          } catch {
            return;
          }
          if (epoch !== legacyGeneration) return;
          remaining = remaining.filter((pending) => pending !== event);
          queue.save(remaining);
        }
      }
    })()
      .catch(console.error)
      .finally(() => {
        if (epoch === legacyGeneration) legacyFlush = undefined;
      });
    return legacyFlush;
  }

  /** Sends the account's plays before it signs out; whatever cannot go now
   *  stays stored for its next sign-in, never sent by another account. */
  async function flushPlaysBeforeLogout(): Promise<void> {
    if (disposed) return;
    const epoch = legacyGeneration;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        // At once: another tab's long upload must not outlast the five seconds.
        playOutbox.flush(true),
        new Promise<void>((resolve) => {
          timeout = setTimeout(resolve, 5000);
        }),
      ]);
    } catch {
      // Reporting must never prevent sign-out.
    } finally {
      clearTimeout(timeout);
      if (!disposed && epoch === legacyGeneration) {
        legacyGeneration++;
        legacyFlush = undefined;
        playOutbox.detach();
      }
    }
  }

  const playOutbox = createOutbox({
    ...ports,
    owner: currentUid,
    online: network,
    onForeground(listener) {
      return appState.subscribe((state) => {
        if (state === "active") listener();
      });
    },
    beforeDrain: flushLegacyPlayQueue,
  });
  const dispose = playOutbox.dispose;
  const forgetAccount = playOutbox.forgetAccount;
  return Object.assign(playOutbox, {
    flushLegacyPlayQueue,
    flushPlaysBeforeLogout,
    forgetAccount() {
      const account = currentUid();
      if (!account) return;
      legacyGeneration++;
      legacyFlush = undefined;
      playOutbox.detach();
      forgetAccount();
      for (const key of legacyKeys) {
        const queue = readLegacy(key);
        queue?.save(queue.events.filter((event) => event.userId !== account));
      }
    },
    dispose() {
      disposed = true;
      legacyGeneration++;
      legacyFlush = undefined;
      dispose();
    },
  });
}
